import React, { useState, useEffect } from 'react';
import { fetchSales, saveSale, deleteSale, fetchCustomers, fetchProducts } from '../../services/api';
import './LubeSales.css';

const today = '04-12-2025';

export default function LubeSales({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ bill_no: '', date: getTodayForUI(), customer_id: '', product: '', qty: '', rate: '', tax_per: 18, payment_mode: 'Cash' });

  useEffect(() => {
    loadInitialData();
    loadData();
  }, [companyId]);

  function getTodayForUI() {
    const d = new Date();
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  }

  function formatDateForAPI(dateStr) {
    if (!dateStr || !dateStr.includes('-')) return dateStr;
    const [y, m, d] = dateStr.split('-');
    return `${d}-${m}-${y}`;
  }

  const loadInitialData = async () => {
    try {
      const [custRes, prodRes] = await Promise.all([
        fetchCustomers(companyId),
        fetchProducts(companyId)
      ]);
      if (custRes.success) setCustomers(custRes.Response || []);
      if (prodRes.success) setAllProducts(prodRes.Response || []);
    } catch (err) {
      console.error("Initial data load error:", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchSales({
        Companyid: companyId,
        Name: "",
        Product: "",
        FromDate: "01-01-2024", // Broad range for lube
        ToDate: formatDateForAPI(getTodayForUI())
      });
      if (res.success) {
        const raw = res.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        // Filter for lubes (assuming common prefix/category if possible)
        // Or could be any product not in fuel
        const lubeList = list.filter(r => 
          !['HSD', 'MS', 'CNG', 'DIESEL', 'PETROL'].includes((r.Product || r.product || '').toUpperCase())
        );
        setSales(lubeList.map(r => ({
          id: r.IDS || r.Recid || r.Saleid,
          billNo: r.BillNo || r.billNo,
          date: r.Date1 || r.date,
          customer: r.Customer || r.customer || 'Cash',
          product: r.Product || r.product,
          qty: parseFloat(r.Qty || r.qty || 0),
          rate: parseFloat(r.Rate || r.rate || 0),
          taxPer: parseFloat(r.TaxPer || r.taxPer || 18),
          tax: parseFloat(r.Tax || r.tax || 0),
          amount: parseFloat(r.Amount || r.amount || 0)
        })));
      }
    } catch (err) {
      console.error("Sales fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.qty || !form.rate || !form.product) return;
    
    try {
      const baseAmt = parseFloat(form.qty) * parseFloat(form.rate);
      const taxAmt = baseAmt * parseFloat(form.tax_per) / 100;
      const customer = form.customer_id ? customers.find(c => (c.Customerid || c.id) == form.customer_id) : null;

      const payload = {
        Companyid: companyId,
        BillNo: form.bill_no || "0",
        Customerid: form.customer_id || "0",
        Name: customer ? (customer.Name || customer.name) : "Cash",
        Product: form.product,
        Qty: form.qty.toString(),
        Rate: form.rate.toString(),
        TaxPer: form.tax_per.toString(),
        Tax: taxAmt.toString(),
        Amount: (baseAmt + taxAmt).toString(),
        PaymentMode: form.payment_mode,
        Date1: formatDateForAPI(form.date),
        IDS: "0"
      };

      const result = await saveSale(payload);
      if (result.success || result.Response === "INSERTED") {
        await loadData();
        setShowForm(false);
        setForm({ bill_no: '', date: getTodayForUI(), customer_id: '', product: '', qty: '', rate: '', tax_per: 18, payment_mode: 'Cash' });
      } else {
        alert(result.message || "Failed to save sale");
      }
    } catch (err) {
      console.error("Lube error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const result = await deleteSale(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadData();
      } else {
        alert(result.message || "Failed to delete record");
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const baseAmt = +form.qty * +form.rate;
  const taxAmt  = baseAmt * +form.tax_per / 100;
  const total   = sales.reduce((s, r) => s + (+r.amount || 0), 0);

  return (
    <div className="lube-sales-page">
      <div className="page-header">
        <h2 className="page-title">Lube Sales</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>＋ New Lube Sale</button>
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">New Lube Sale</div>
          <div className="form-row">
            <div className="form-group"><label>Bill No.</label><input className="form-control" value={form.bill_no} onChange={e => setForm({ ...form, bill_no: e.target.value })} placeholder="LB-0325" /></div>
            <div className="form-group"><label>Date</label><input className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group">
              <label>Customer</label>
              <select className="form-control" value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })}>
                <option value="">Cash</option>
                {customers.map(c => <option key={c.Customerid || c.id} value={c.Customerid || c.id}>{c.Name || c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Product</label>
              <select className="form-control" value={form.product} onChange={e => {
                const p = allProducts.find(prod => prod.Name === e.target.value);
                setForm({ ...form, product: e.target.value, rate: p ? p.Rate : form.rate });
              }}>
                <option value="">Select Product</option>
                {allProducts.filter(p => !['HSD', 'MS', 'CNG'].includes(p.Name.toUpperCase())).map(p => <option key={p.Productid} value={p.Name}>{p.Name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Qty</label><input type="number" className="form-control" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} /></div>
            <div className="form-group"><label>Rate (₹)</label><input type="number" className="form-control" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} /></div>
            <div className="form-group"><label>Tax %</label><input type="number" className="form-control" value={form.tax_per} onChange={e => setForm({ ...form, tax_per: e.target.value })} /></div>
            {form.qty && form.rate && (
              <div className="form-group">
                <label>Total (incl. tax)</label>
                <div style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontWeight: 700, color: 'var(--secondary)' }}>
                  ₹{(baseAmt + taxAmt).toFixed(2)} <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>(Tax: ₹{taxAmt.toFixed(2)})</span>
                </div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
  <button className="btn btn-primary" onClick={handleSave}>
    <i className="bi bi-save me-1"></i> Save
  </button>
  <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
    <i className="bi bi-x-lg me-1"></i> Cancel
  </button>
</div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">Lube Sales — {sales.length} records &nbsp;|&nbsp; Total: ₹{total.toLocaleString()}</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-center">Bill No.</th>
                <th className="text-center">Date</th>
                <th className="text-left">Customer</th>
                <th className="text-left">Product</th>
                <th className="text-center">Qty</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-center">Tax %</th>
                <th className="text-right">Tax (₹)</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {sales.map((s, i) => (
                <tr key={s.id}>
                  <td className="text-center" style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>{s.billNo}</td>
                  <td className="text-center">{s.date}</td>
                  <td className="text-left" style={{ fontWeight: 600 }}>{s.customer || 'Cash'}</td>
                  <td className="text-left">{s.product}</td>
                  <td className="text-center">{s.qty}</td>
                  <td className="text-right">₹{parseFloat(s.rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-center">{s.taxPer}%</td>
                  <td className="text-right">₹{parseFloat(s.tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right" style={{ fontWeight: 700 }}>₹{parseFloat(s.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-center">
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}